import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, DollarSign, Clock, HelpCircle } from 'lucide-react';

const HUE = {
  strong: '#22d3ee',   // 80+
  good:   '#a78bfa',   // 65+
  okay:   '#38bdf8',   // 45+
  weak:   '#f472b6',   // below
};

function bandFor(pct) {
  if (pct >= 80) return 'strong';
  if (pct >= 65) return 'good';
  if (pct >= 45) return 'okay';
  return 'weak';
}
function labelFor(pct) {
  if (pct >= 80) return 'Strong match';
  if (pct >= 65) return 'Good match';
  if (pct >= 45) return 'Worth exploring';
  return 'Stretch';
}

/**
 * PathFitDashboard, the shared "is this a good fit?" strip used on every
 * major decision surface (College, Skilled Trades, Military, Entrepreneurship).
 *
 * Props:
 *   pathLabel     , the pathway name for the UI ("this college", "the trades", etc.)
 *   fit           , { overall, career, financial, timeline }  (0-100 each)
 *   reasons       , optional string[] of "why we scored it this way"
 */
export default function PathFitDashboard({ pathLabel = 'this path', fit, reasons = [] }) {
  const f = {
    overall:   Math.round(fit?.overall ?? 0),
    career:    Math.round(fit?.career ?? 0),
    financial: Math.round(fit?.financial ?? 0),
    timeline:  Math.round(fit?.timeline ?? 0),
  };
  const band = bandFor(f.overall);
  const hue = HUE[band];

  const chips = [
    { key: 'career',    label: 'Career match',    hint: 'Your interests + strengths against typical roles', pct: f.career,    icon: Sparkles, hue: HUE[bandFor(f.career)] },
    { key: 'financial', label: 'Financial fit',   hint: 'Cost vs. your household aid picture',              pct: f.financial, icon: DollarSign, hue: HUE[bandFor(f.financial)] },
    { key: 'timeline',  label: 'Timeline fit',    hint: 'How long until you\'re earning',                    pct: f.timeline,  icon: Clock,    hue: HUE[bandFor(f.timeline)] },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="path-fit-dashboard"
      className="relative overflow-hidden rounded-2xl border p-5"
      style={{
        borderColor: `${hue}44`, background: `${hue}0a`,
        boxShadow: `0 30px 80px -60px ${hue}`,
      }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl opacity-25"
        style={{ background: `radial-gradient(circle, ${hue}, transparent 65%)` }} />

      <div className="relative flex flex-wrap items-center gap-5">
        {/* Overall, one number, big */}
        <div className="flex items-center gap-4">
          <div className="rounded-2xl border px-4 py-3 text-center"
            style={{ borderColor: `${hue}66`, background: `${hue}15` }}>
            <p className="text-3xl font-bold" style={{ color: hue }} data-testid="fit-overall">{f.overall}%</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-0.5">Overall fit</p>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">{labelFor(f.overall)} for {pathLabel}</p>
            <p className="text-[11px] text-slate-300 max-w-md leading-relaxed">
              Based on your interests, strengths, financial picture, and timeline preferences.
            </p>
          </div>
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-slate-300 cursor-help"
            title="Career match: your interests/strengths against typical roles. Financial fit: cost vs your family aid picture. Timeline fit: how quickly you'd be earning. Overall = weighted average.">
            <HelpCircle className="h-3 w-3" /> What's this based on?
          </span>
        </div>

        {/* Sub-fit chips, right-aligned on wide screens */}
        <div className="ml-auto grid grid-cols-3 gap-2">
          {chips.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="rounded-xl border px-3 py-2 min-w-[120px]"
                style={{ borderColor: `${c.hue}44`, background: `${c.hue}0a` }}
                data-testid={`fit-${c.key}`}
                title={c.hint}>
                <div className="flex items-center justify-between gap-2">
                  <Icon className="h-3.5 w-3.5" style={{ color: c.hue }} />
                  <p className="text-sm font-bold" style={{ color: c.hue }}>{c.pct}%</p>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-0.5">{c.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {reasons.length > 0 && (
        <div className="relative mt-4 flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
          <TrendingUp className="h-3 w-3 text-cyan-300" />
          <span className="text-slate-300">Why:</span>
          {reasons.slice(0, 4).map((r, i) => (
            <span key={i} className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5">{r}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}
